/**
 * Session Error Boundary Component
 *
 * This error boundary is specifically designed for BrightAgent sessions.
 * It catches errors that occur within a session and provides recovery options:
 * - Start a new session (navigates to /workspace/:workspaceId/brightagent)
 * - View all sessions (navigates to sessions list)
 * - Try again (resets the error boundary state)
 *
 * Usage: This should wrap the main chat/session content in the BrightBot component
 * to provide session-specific error recovery without affecting other parts of the app.
 *
 * Props:
 * - onSessionReset?: Optional callback to reset session state when starting a new session
 */

import React, { Component, ErrorInfo, ReactNode, useEffect } from "react";
import { Box, Button, Typography, Paper, Alert } from "@mui/material";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { BrightBotSmileIcon } from "../../common/Icons/BrightBotSmileIcon";
import { brighthiveTheme } from "../../theme/theme";

interface Props {
  children?: ReactNode;
  onSessionReset?: () => void;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
}

// We need a wrapper component to use hooks
const SessionErrorBoundaryWrapper = ({ children, onSessionReset }: Props) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { workspaceId, threadId } = useParams();

  const handleNewSession = () => {
    // Clear any session state if needed
    if (onSessionReset) {
      onSessionReset();
    }
    // Navigate to new session - force a reload by navigating away first if we're already on brightagent
    const currentPath = window.location.pathname;
    if (currentPath.includes("/brightagent")) {
      // Navigate to a different route first, then to new session
      navigate(`/workspace/${workspaceId}/home`);
      setTimeout(() => {
        navigate(`/workspace/${workspaceId}/brightagent`);
      }, 100);
    } else {
      navigate(`/workspace/${workspaceId}/brightagent`);
    }
  };

  const handleGoToSessions = () => {
    // Navigate to sessions list - same logic as new session since both go to the main brightagent page
    const currentPath = window.location.pathname;
    if (currentPath.includes("/brightagent")) {
      navigate(`/workspace/${workspaceId}/home`);
      setTimeout(() => {
        navigate(`/workspace/${workspaceId}/brightagent`);
      }, 100);
    } else {
      navigate(`/workspace/${workspaceId}/brightagent`);
    }
  };

  return (
    <SessionErrorBoundaryClass
      onNewSession={handleNewSession}
      onGoToSessions={handleGoToSessions}
      resetTrigger={`${location.pathname}-${threadId || "new"}`}
    >
      {children}
    </SessionErrorBoundaryClass>
  );
};

interface ClassProps {
  children?: ReactNode;
  onNewSession: () => void;
  onGoToSessions: () => void;
  resetTrigger?: string; // Add this to force reset from parent
}

class SessionErrorBoundaryClass extends Component<ClassProps, State> {
  public state: State = {
    hasError: false,
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("Session Error Boundary caught an error:", error, errorInfo);

    // Log to Sentry if available with session context
    if ((window as any).Sentry) {
      (window as any).Sentry.captureException(error, {
        tags: {
          errorBoundary: "session",
          component: "BrightAgentSession",
        },
        contexts: {
          react: {
            componentStack: errorInfo.componentStack,
          },
        },
      });
    }

    this.setState({
      error,
      errorInfo,
    });
  }

  public componentDidUpdate(prevProps: ClassProps) {
    // Reset error state if resetTrigger prop changes
    if (
      prevProps.resetTrigger !== this.props.resetTrigger &&
      this.state.hasError
    ) {
      this.setState({
        hasError: false,
        error: undefined,
        errorInfo: undefined,
      });
    }
  }

  private handleRetry = () => {
    this.setState({ hasError: false, error: undefined, errorInfo: undefined });
  };

  public render() {
    if (this.state.hasError) {
      return (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            height: "100%",
            width: "100%",
            p: 4,
            textAlign: "center",
          }}
        >
          <Paper
            elevation={2}
            sx={{
              p: 4,
              maxWidth: 600,
              width: "100%",
              backgroundColor: brighthiveTheme.palette.common.white,
              borderRadius: 2,
            }}
          >
            <Box sx={{ mb: 3 }}>
              <BrightBotSmileIcon
                style={{
                  fontSize: 48,
                  color: brighthiveTheme.palette.warning.main,
                }}
              />
            </Box>

            <Typography variant="h5" component="h2" gutterBottom color="error">
              Session Error
            </Typography>

            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              Something went wrong with your current BrightAgent session. You
              can try to recover or start fresh.
            </Typography>

            <Alert severity="warning" sx={{ mb: 3, textAlign: "left" }}>
              <Typography variant="body2">
                Don't worry - your other sessions are still safe. This error
                only affects the current session.
              </Typography>
            </Alert>

            <Box
              sx={{
                display: "flex",
                gap: 2,
                justifyContent: "center",
                flexWrap: "wrap",
              }}
            >
              <Button
                variant="contained"
                color="primary"
                onClick={this.props.onNewSession}
                size="large"
              >
                Start New Session
              </Button>
              <Button
                variant="text"
                color="secondary"
                onClick={this.handleRetry}
                size="large"
              >
                Try Again
              </Button>
            </Box>

            {process.env.NODE_ENV === "development" && this.state.error && (
              <Box sx={{ mt: 4, textAlign: "left" }}>
                <Typography variant="h6" gutterBottom>
                  Error Details (Development Mode):
                </Typography>
                <Box
                  component="pre"
                  sx={{
                    backgroundColor: "#f5f5f5",
                    p: 2,
                    borderRadius: 1,
                    overflow: "auto",
                    fontSize: "0.75rem",
                    maxHeight: "150px",
                  }}
                >
                  {this.state.error.toString()}
                  {this.state.errorInfo?.componentStack}
                </Box>
              </Box>
            )}
          </Paper>
        </Box>
      );
    }

    return this.props.children;
  }
}

export default SessionErrorBoundaryWrapper;
